import type { ProjectRole } from "@walkie/protocol"
import { useEffect, useRef, useState } from "react"
import { api, errorText } from "../api/client"
import type { GithubSettings, ProjectSummary } from "../api/types"
import { age } from "../lib/time"
import { currentProject, goHome, loadMe, loadSnapshot, toast } from "../store/actions"
import { setUi, useStore } from "../store/store"
import { CopyButton, PersonSwatch, useDismiss } from "./ui"

const REPO = /^[\w.-]+\/[\w.-]+$/
/** Accepts `owner/repo`, a github.com URL, or a git remote. */
export function parseRepo(text: string): string | null {
	const t = text
		.trim()
		.replace(/^git@github\.com:/i, "")
		.replace(/^https?:\/\/(www\.)?github\.com\//i, "")
		.replace(/\.git$/i, "")
		.replace(/\/+$/, "")
		.split("/")
		.slice(0, 2)
		.join("/")
	return REPO.test(t) ? t.toLowerCase() : null
}

export function SettingsDrawer() {
	const close = () => setUi({ settings: false })
	useDismiss(close)
	const project = useStore(s => currentProject(s))
	const role = useStore(s => s.role)
	const admin = role === "admin"
	if (!project) return null

	return (
		<>
			<div className="scrim clear" onMouseDown={close} />
			<aside className="slide" role="dialog" aria-label="Project settings">
				<div className="slide-h">
					{project.name} <span className="dim">settings</span>
					<button className="icon-btn" aria-label="Close (Esc)" onClick={close}>
						×
					</button>
				</div>
				<div className="slide-body">
					<Details project={project} canEdit={admin} />
					{admin ? (
						<Github project={project} />
					) : (
						<div className="blk" id="settings-github">
							<div className="blk-h">
								<span className="lbl">GitHub repo</span>
							</div>
							<div className="blk-row">
								{project.githubRepos.length ? (
									<span className="mono">{project.githubRepos.join(", ")}</span>
								) : (
									<span className="dim">
										Not connected yet. A project admin can connect it.
									</span>
								)}
							</div>
						</div>
					)}
					<People project={project} canEdit={admin} />
					{admin && <Invite project={project} />}
					{admin && <DangerZone project={project} />}
				</div>
			</aside>
		</>
	)
}

function Details({ project, canEdit }: { project: ProjectSummary; canEdit: boolean }) {
	const [name, setName] = useState(project.name)
	const [desc, setDesc] = useState(project.description ?? "")
	useEffect(() => {
		setName(project.name)
		setDesc(project.description ?? "")
	}, [project.id, project.name, project.description])
	const save = async (patch: Parameters<typeof api.patchProject>[1]) => {
		try {
			await api.patchProject(project.id, patch)
			await loadMe()
		} catch (e) {
			toast(errorText(e))
		}
	}
	return (
		<div className="blk">
			<div className="blk-h">
				<span className="lbl">Project</span>
			</div>
			<div className="blk-row">
				<span className="k">Name</span>
				<input
					className="input grow"
					value={name}
					disabled={!canEdit}
					aria-label="Project name"
					onChange={e => setName(e.target.value)}
					onBlur={() =>
						name.trim() &&
						name.trim() !== project.name &&
						void save({ name: name.trim() })
					}
					onKeyDown={e =>
						e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()
					}
				/>
			</div>
			<div className="blk-row">
				<span className="k">About</span>
				<input
					className="input grow"
					value={desc}
					disabled={!canEdit}
					placeholder={canEdit ? "What this project is for (optional)" : ""}
					aria-label="Project description"
					onChange={e => setDesc(e.target.value)}
					onBlur={() =>
						desc.trim() !== (project.description ?? "") &&
						void save({ description: desc.trim() || null })
					}
					onKeyDown={e =>
						e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()
					}
				/>
			</div>
		</div>
	)
}

const ROLE_HELP: Record<string, string> = {
	viewer: "reads everything, can't post",
	member: "posts, claims and answers",
	admin: "also connects the repo and manages people",
}

function People({ project, canEdit }: { project: ProjectSummary; canEdit: boolean }) {
	const people = useStore(s => s.people)
	const agents = useStore(s => s.agents)
	const meId = useStore(s => s.me?.user.id)
	// The people who own the project's workspace are always admins of it.
	const [owners, setOwners] = useState<Set<string>>(new Set())
	useEffect(() => {
		api
			.teamMembers(project.teamId)
			.then(r =>
				setOwners(new Set(r.members.filter(m => m.role !== "member").map(m => m.userId))),
			)
			.catch(() => undefined)
	}, [project.teamId])
	const setRole = async (userId: string, handle: string, role: ProjectRole | null) => {
		if (
			role === null &&
			!confirm(`Remove ${handle} from ${project.name}? Their chats lose access.`)
		)
			return
		try {
			await api.setProjectRole(project.id, userId, role)
			await loadSnapshot(project.id)
		} catch (e) {
			toast(errorText(e))
		}
	}
	const chats = (userId: string) =>
		Object.values(agents).filter(a => a.userId === userId && a.state !== "ended")

	return (
		<div className="blk" id="settings-people">
			<div className="blk-h">
				<span className="lbl">People</span>
				<span className="count">{people.length}</span>
			</div>
			{people.map(m => {
				const live = chats(m.userId)
				const on = live.filter(a => a.state === "active" || a.state === "idle").length
				const fixed = owners.has(m.userId) || m.userId === meId || !canEdit
				return (
					<div key={m.userId} className="blk-row">
						<PersonSwatch handle={m.handle} />
						<b>{m.handle}</b>
						<span className="dim ell grow">
							{m.name}
							{live.length > 0 && (
								<span className={on ? "ok" : ""}>
									{" "}
									· {on}/{live.length} chats online
								</span>
							)}
						</span>
						{fixed ? (
							<span className="chip" title={ROLE_HELP[m.role]}>
								{owners.has(m.userId) ? "owner" : m.role}
							</span>
						) : (
							<select
								className="select"
								style={{ height: 20, fontSize: 11 }}
								aria-label={`Role for ${m.handle}`}
								title={ROLE_HELP[m.role]}
								value={m.role}
								onChange={e =>
									void setRole(
										m.userId,
										m.handle,
										e.target.value === "remove" ? null : (e.target.value as ProjectRole),
									)
								}
							>
								<option value="viewer">viewer</option>
								<option value="member">member</option>
								<option value="admin">admin</option>
								<option value="remove">Remove…</option>
							</select>
						)}
					</div>
				)
			})}
			{people.length <= 1 && (
				<div className="blk-row dim">Just you so far. Invite someone below.</div>
			)}
		</div>
	)
}

function Invite({ project }: { project: ProjectSummary }) {
	const [email, setEmail] = useState("")
	const [projectRole, setProjectRole] = useState<ProjectRole>("member")
	const [url, setUrl] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)
	const submit = async () => {
		setBusy(true)
		try {
			const r = await api.invite(project.teamId, {
				email: email.trim() || undefined,
				projectRoles: { [project.id]: projectRole },
			})
			setUrl(r.url)
		} catch (e) {
			toast(errorText(e))
		} finally {
			setBusy(false)
		}
	}
	return (
		<div className="blk" id="settings-invite">
			<div className="blk-h">
				<span className="lbl">Invite someone</span>
			</div>
			<form
				className="blk-row"
				style={{ flexWrap: "wrap" }}
				onSubmit={e => {
					e.preventDefault()
					void submit()
				}}
			>
				<input
					className="input grow"
					type="email"
					placeholder="their email (optional)"
					value={email}
					onChange={e => setEmail(e.target.value)}
					aria-label="Invitee email"
				/>
				<select
					className="select"
					value={projectRole}
					onChange={e => setProjectRole(e.target.value as ProjectRole)}
					aria-label="Their role in this project"
				>
					<option value="member">as member</option>
					<option value="viewer">as viewer</option>
					<option value="admin">as admin</option>
				</select>
				<button className="btn btn-primary" disabled={busy}>
					Create invite link
				</button>
			</form>
			{url ? (
				<div
					className="blk-row"
					style={{ flexDirection: "column", alignItems: "stretch" }}
				>
					<div className="copy-box">
						<input
							className="input"
							readOnly
							value={url}
							onFocus={e => e.currentTarget.select()}
							aria-label="Invitation link"
						/>
						<CopyButton text={url} />
					</div>
					<span className="dim" style={{ fontSize: 11 }}>
						Send it to them privately. It's shown once, works for one person and expires
						in 7 days. They sign up and land in {project.name}.
					</span>
				</div>
			) : (
				<div className="blk-row dim" style={{ fontSize: 11 }}>
					They sign up with the link and land in this project, then connect their own
					Claude or Codex. With an email, only that address can use it.
				</div>
			)}
		</div>
	)
}

// --- GitHub ---------------------------------------------------------------------------------

const EVENTS_HELP =
	"Pull requests, pushes, check runs, check suites, issue comments and pull request reviews (or simply “Send me everything”)."

function Github({ project }: { project: ProjectSummary }) {
	const [g, setG] = useState<GithubSettings | null>(null)
	const [repoText, setRepoText] = useState("")
	const [logins, setLogins] = useState("")
	const [busy, setBusy] = useState(false)
	const [manual, setManual] = useState(false)
	const [more, setMore] = useState(false)
	const tick = useRef(0)

	const load = () =>
		api
			.github(project.id)
			.then(r => {
				setG(r)
				setLogins(
					Object.entries(r.logins)
						.map(([k, v]) => `${k} = ${v}`)
						.join("\n"),
				)
				return r
			})
			.catch(e => (toast(errorText(e)), null))

	useEffect(() => {
		void load()
		return () => clearInterval(poll.current)
	}, [project.id])

	// While a webhook is being created look again every 2 s; while GitHub has not
	// called yet, every 10 s. Stops once events arrive or the drawer closes.
	const poll = useRef<ReturnType<typeof setInterval>>(undefined)
	const pending = g?.hookSetup?.ok === null
	const waiting = !!g && g.repos.length > 0 && !g.lastDelivery
	useEffect(() => {
		clearInterval(poll.current)
		if (!pending && !waiting) return
		poll.current = setInterval(() => {
			tick.current++
			if (document.visibilityState === "visible" && (pending || tick.current % 5 === 0))
				void load()
		}, 2000)
		return () => clearInterval(poll.current)
	}, [pending, waiting, project.id])

	// GitHub's first call arrived: the sidebar's repo dot follows.
	const live = !!g?.lastDelivery
	useEffect(() => {
		if (live && !project.githubLive) void loadMe()
	}, [live, project.githubLive])

	const online = g?.devices?.find(d => d.online)
	const repos = g?.repos ?? []

	const autoSetup = async (repo: string) => {
		try {
			const r = await api.githubSetup(project.id, repo, online?.id)
			toast(`Creating the webhook with gh on ${r.deviceName ?? "your computer"}…`, "info")
			await load()
		} catch (e) {
			setManual(true)
			toast(errorText(e))
		}
	}

	const link = async () => {
		const repo = parseRepo(repoText)
		if (!repo) return toast("Use owner/repo, or paste the repository's GitHub URL")
		setBusy(true)
		try {
			if (!repos.includes(repo))
				await api.patchProject(project.id, { githubRepos: [...repos, repo] })
			setRepoText("")
			await Promise.all([load(), loadMe()])
			if (online) await autoSetup(repo)
			else setManual(true)
		} catch (e) {
			toast(errorText(e))
		} finally {
			setBusy(false)
		}
	}

	const unlink = async (repo: string) => {
		try {
			await api.patchProject(project.id, { githubRepos: repos.filter(r => r !== repo) })
			await Promise.all([load(), loadMe()])
		} catch (e) {
			toast(errorText(e))
		}
	}

	const saveLogins = async () => {
		try {
			const githubLogins = Object.fromEntries(
				logins
					.split("\n")
					.map(l => l.split("=").map(s => s.trim().replace(/^@/, "")))
					.filter(p => p[0] && p[1]),
			) as Record<string, string>
			await api.patchProject(project.id, { githubLogins })
			toast("Saved", "info")
		} catch (e) {
			toast(errorText(e))
		}
	}

	if (!g)
		return (
			<div className="blk" id="settings-github">
				<div className="blk-h">
					<span className="lbl">GitHub repo</span>
				</div>
				<div className="blk-row dim">Loading…</div>
			</div>
		)

	const setup = g.hookSetup
	const status = g.lastDelivery ? (
		<span className="ok">
			● Connected — last event {g.lastDelivery.event}
			{g.lastDelivery.repo ? ` from ${g.lastDelivery.repo}` : ""}{" "}
			{age(g.lastDelivery.at, Date.now())} ago
		</span>
	) : setup?.ok === null ? (
		<span className="warn">◌ Creating the webhook on GitHub…</span>
	) : setup?.ok === false ? (
		<span className="danger">
			✕ Webhook setup failed: {setup.error ?? "unknown error"}
		</span>
	) : setup?.ok ? (
		<span className="warn">◌ Webhook created — waiting for GitHub's first call</span>
	) : repos.length ? (
		<span className="warn">◌ Not hearing from GitHub yet — the webhook isn't set up</span>
	) : null

	return (
		<div className="blk" id="settings-github">
			<div className="blk-h">
				<span className="lbl">GitHub repo</span>
			</div>
			{repos.length > 0 && (
				<div className="blk-row" style={{ fontSize: 11 }}>
					{status}
				</div>
			)}
			{repos.length === 0 && (
				<div className="blk-row dim" style={{ fontSize: 11 }}>
					Connect the repo this project works in. One person does this once for everyone:
					merges, failed checks and reviews then show up in the rooms, and blockers can
					wait for a PR to merge or a check to pass.
				</div>
			)}
			{repos.map(r => (
				<div key={r} className="blk-row">
					<a
						className="mono grow ell"
						href={`https://github.com/${r}`}
						target="_blank"
						rel="noreferrer"
					>
						{r}
					</a>
					{!g.lastDelivery && online && !(setup?.ok === null && setup.repo === r) && (
						<button className="btn" onClick={() => void autoSetup(r)}>
							Set up webhook
						</button>
					)}
					<button
						className="icon-btn"
						aria-label={`Unlink ${r}`}
						title="Unlink"
						onClick={() => void unlink(r)}
					>
						×
					</button>
				</div>
			))}
			{(repos.length === 0 || more) && (
				<form
					className="blk-row"
					onSubmit={e => {
						e.preventDefault()
						void link()
					}}
				>
					<input
						className="input grow mono"
						placeholder="owner/repo or https://github.com/owner/repo"
						value={repoText}
						onChange={e => setRepoText(e.target.value)}
						aria-label="GitHub repository"
					/>
					<button className="btn btn-primary" disabled={busy || !repoText.trim()}>
						Connect
					</button>
				</form>
			)}
			{repos.length > 0 && (
				<div className="blk-row dim" style={{ fontSize: 11, flexWrap: "wrap" }}>
					<span className="grow">
						{online ? (
							<>
								Webhooks are created with the GitHub CLI (<code>gh</code>) on{" "}
								{online.name}; you need admin rights on the repo.
							</>
						) : (
							<>
								No computer of yours is online with Walkie, so set the webhook up by hand.
							</>
						)}
					</span>
					<button className="linkish" onClick={() => setManual(!manual)}>
						{manual ? "Hide manual steps" : "Manual steps"}
					</button>
					{!more && (
						<button
							className="linkish"
							style={{ marginLeft: 10 }}
							onClick={() => setMore(true)}
						>
							Add another repo
						</button>
					)}
				</div>
			)}
			{manual && repos.length > 0 && (
				<ol className="blk-row steps">
					<li>
						Open{" "}
						<a
							href={`https://github.com/${repos[0]}/settings/hooks/new`}
							target="_blank"
							rel="noreferrer"
						>
							{repos[0]} → Settings → Webhooks → Add webhook
						</a>
					</li>
					<li>
						Payload URL
						<div className="copy-box">
							<input
								className="input"
								readOnly
								value={g.webhookUrl}
								aria-label="Payload URL"
							/>
							<CopyButton text={g.webhookUrl} />
						</div>
					</li>
					<li>
						Content type: <b>application/json</b>
					</li>
					<li>
						Secret
						<div className="copy-box">
							<input
								className="input"
								readOnly
								type="password"
								value={g.secret}
								aria-label="Webhook secret"
							/>
							<CopyButton text={g.secret} />
						</div>
					</li>
					<li>Events: {EVENTS_HELP}</li>
					<li>Add webhook. This page turns green when GitHub's first call arrives.</li>
				</ol>
			)}
			{repos.length > 0 && (
				<details className="blk-row adv">
					<summary>GitHub usernames → Walkie handles</summary>
					<div className="dim" style={{ fontSize: 11 }}>
						So “review requested from octocat” reaches the right person. One per line.
					</div>
					<textarea
						className="textarea mono"
						rows={3}
						value={logins}
						onChange={e => setLogins(e.target.value)}
						placeholder="octocat = ada"
					/>
					<div className="row" style={{ justifyContent: "flex-end" }}>
						<button className="btn" onClick={() => void saveLogins()}>
							Save
						</button>
					</div>
				</details>
			)}
		</div>
	)
}

function DangerZone({ project }: { project: ProjectSummary }) {
	const [busy, setBusy] = useState(false)
	const del = async () => {
		const typed = prompt(
			`Delete "${project.name}" for everyone?\n\nIt disappears from everyone's projects, and every computer connected to it is disconnected. Type the project name to confirm.`,
		)
		if (typed === null) return
		if (typed.trim() !== project.name)
			return toast("The name didn't match, so nothing was deleted")
		setBusy(true)
		try {
			await api.deleteProject(project.id)
			toast(`Deleted ${project.name}`, "info")
			goHome()
			await loadMe()
		} catch (e) {
			toast(errorText(e))
			setBusy(false)
		}
	}
	return (
		<div className="blk danger-zone">
			<div className="blk-row">
				<span className="grow">
					<b>Delete project</b>
					<span className="dim" style={{ display: "block", fontSize: 11 }}>
						Removes it for everyone and disconnects every computer connected to it.
					</span>
				</span>
				<button className="btn btn-danger" disabled={busy} onClick={() => void del()}>
					Delete…
				</button>
			</div>
		</div>
	)
}
